import type {
  IacrmApiConfig,
  IacrmListEnvelope,
  IacrmDetailEnvelope,
  IacrmRequestLogEntry,
  IacrmService,
  IacrmClient,
  IacrmPipelineProspect,
  IacrmPipelineStage,
  IacrmPipelineStageSummary,
  IacrmInvoice,
  IacrmInvoiceSummary,
  IacrmPlatformBusiness,
} from '../../types/iacrm'
import { logIacrmActivity } from './activityLog'
import { apiRequest } from '@/lib/api'

// ---------------------------------------------------------------------------
// Config persistence (localStorage — scoped per business / platform)
// ---------------------------------------------------------------------------

const SCOPE_KEY = 'iacrm_scope'
export const IACRM_CONFIG_EVENT = 'iacrm-config-updated'


/**
 * Returns the active IACRM scope: a business UUID or 'platform' for superadmin.
 * Defaults to 'platform' if not set.
 */
function getActiveScope(): string {
  return localStorage.getItem(SCOPE_KEY) ?? 'platform'
}

/** Call this after a successful login with the user's primary business ID (or null for platform). */
export function setIacrmScope(businessId: string | null) {
  const scope = businessId ?? 'platform'
  localStorage.setItem(SCOPE_KEY, scope)
  window.dispatchEvent(new CustomEvent(IACRM_CONFIG_EVENT))
}

function getStorageKey(): string {
  return `iacrm_api_config_${getActiveScope()}`
}

export function getIacrmConfig(): IacrmApiConfig | null {
  try {
    const raw = localStorage.getItem(getStorageKey())
    if (!raw) return null
    const parsed = JSON.parse(raw) as IacrmApiConfig
    return parsed
  } catch {
    return null
  }
}

export function saveIacrmConfig(config: IacrmApiConfig) {
  localStorage.setItem(getStorageKey(), JSON.stringify(config))
  window.dispatchEvent(new CustomEvent(IACRM_CONFIG_EVENT))
}

export function clearIacrmConfig() {
  localStorage.removeItem(getStorageKey())
  window.dispatchEvent(new CustomEvent(IACRM_CONFIG_EVENT))
}

export function hasIacrmConfig(config: IacrmApiConfig | null = getIacrmConfig()) {
  return Boolean(config?.base_url && config.api_key.trim().length > 0)
}

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '')
}

function normalizeApiBaseUrl(baseUrl: string): string {
  const normalized = trimTrailingSlash(baseUrl)
  return normalized.replace(/\/(services|products)$/i, '')
}

function summarizeErrorMessage(message: string, maxLength = 1800): string {
  const normalized = message.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }

  return `${normalized.slice(0, maxLength - 1)}…`
}

// ---------------------------------------------------------------------------
// IACRM HTTP client (separate from the Laravel apiRequest)
// ---------------------------------------------------------------------------

class IacrmApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'IacrmApiError'
    this.status = status
  }
}

function parseJsonBody(body?: BodyInit | null): Record<string, unknown> | unknown[] {
  if (typeof body !== 'string' || body.trim() === '') {
    return {}
  }

  try {
    const parsed = JSON.parse(body)
    return typeof parsed === 'object' && parsed !== null ? parsed : { value: parsed }
  } catch {
    return { raw: body }
  }
}

function normalizePayload(payload: unknown): Record<string, unknown> | unknown[] {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object') return payload as Record<string, unknown>
  if (payload === null || payload === undefined) return {}
  return { value: payload }
}

async function recordFrontendIacrmRequestLog(entry: {
  source: string
  direction: 'pull' | 'push' | 'test'
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  endpoint: string
  status: 'success' | 'failed'
  status_code?: number
  duration_ms?: number
  error_message?: string
  request_payload?: Record<string, unknown> | unknown[]
  response_payload?: Record<string, unknown> | unknown[]
}) {
  try {
    const endpoint = entry.endpoint.length > 1000
      ? `${entry.endpoint.slice(0, 999)}…`
      : entry.endpoint

    await apiRequest<{ data: IacrmRequestLogEntry }>('/v1/iacrm/request-logs/frontend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...entry,
        endpoint,
        error_message: entry.error_message ? summarizeErrorMessage(entry.error_message) : undefined,
        source: entry.source,
        request_payload: entry.request_payload ?? {},
        response_payload: entry.response_payload ?? {},
        requested_at: new Date().toISOString(),
      }),
    })
  } catch {
    // Never block the IACRM UI because of observability logging failures.
  }
}

async function iacrmRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const config = getIacrmConfig()
  if (!hasIacrmConfig(config)) throw new IacrmApiError(0, 'IACRM API not configured')
  const resolvedConfig = config as IacrmApiConfig

  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  headers.set('X-IACRM-API-Key', resolvedConfig.api_key.trim())

  const url = `${normalizeApiBaseUrl(resolvedConfig.base_url)}${path.startsWith('/') ? path : `/${path}`}`
  const method = (init.method ?? 'GET').toUpperCase() as 'GET' | 'POST' | 'PATCH' | 'DELETE'
  const type = path.includes('/auth/') ? 'test' : method === 'GET' ? 'pull' : 'push'
  const t0 = Date.now()
  const requestPayload = parseJsonBody(init.body)

  try {
    const response = await fetch(url, { ...init, headers })
    const duration_ms = Date.now() - t0

    if (!response.ok) {
      const text = await response.text().catch(() => 'Unknown error')
      logIacrmActivity({ type: 'error', method, endpoint: path, status: 'failed', status_code: response.status, duration_ms })
      await recordFrontendIacrmRequestLog({
        source: type === 'test' ? 'frontend.test' : 'frontend.direct',
        direction: type,
        method,
        endpoint: path,
        status: 'failed',
        status_code: response.status,
        duration_ms,
        error_message: summarizeErrorMessage(text),
        request_payload: requestPayload,
        response_payload: { body: summarizeErrorMessage(text, 4000) },
      })
      throw new IacrmApiError(response.status, text)
    }

    const payload = (await response.json()) as T
    logIacrmActivity({ type, method, endpoint: path, status: 'success', status_code: response.status, duration_ms })
    await recordFrontendIacrmRequestLog({
      source: type === 'test' ? 'frontend.test' : 'frontend.direct',
      direction: type,
      method,
      endpoint: path,
      status: 'success',
      status_code: response.status,
      duration_ms,
      request_payload: requestPayload,
      response_payload: normalizePayload(payload),
    })
    return payload
  } catch (err) {
    if (err instanceof IacrmApiError) throw err
    logIacrmActivity({ type: 'error', method, endpoint: path, status: 'failed', duration_ms: Date.now() - t0 })
    await recordFrontendIacrmRequestLog({
      source: type === 'test' ? 'frontend.test' : 'frontend.direct',
      direction: type,
      method,
      endpoint: path,
      status: 'failed',
      duration_ms: Date.now() - t0,
      error_message: summarizeErrorMessage(err instanceof Error ? err.message : 'Request failed'),
      request_payload: requestPayload,
      response_payload: {},
    })
    throw err
  }
}

function extractCatalogRows(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) {
    return payload.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>
  const directCollections = ['data', 'items', 'products', 'services', 'results']

  for (const key of directCollections) {
    const value = record[key]
    if (Array.isArray(value)) {
      return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    }
  }

  return []
}

function firstString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim()
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value)
    }
  }

  return null
}

function normalizeClientStatus(row: Record<string, unknown>): string {
  const explicitStatus = firstString(row, ['status', 'crm_status'])

  if (!explicitStatus) {
    return 'active'
  }

  const normalized = explicitStatus.trim().toLowerCase()
  if (['inactive', 'archived', 'deleted', 'blocked', 'disabled', 'lost'].includes(normalized)) {
    return 'inactive'
  }

  return 'active'
}

function normalizeClientItem(row: Record<string, unknown>) {
  const iacrmId = firstString(row, ['id', 'iacrm_id', 'uuid', 'external_id'])
  if (!iacrmId) {
    return null
  }

  return {
    iacrm_id: iacrmId,
    company_name: firstString(row, ['company_name', 'company', 'business_name']) ?? '',
    contact_name:
      firstString(row, ['contact_name', 'name', 'full_name', 'agent_fullname']) ??
      firstString(row, ['company_name', 'company', 'business_name']) ??
      firstString(row, ['contact_email', 'email']) ??
      'Client sans nom',
    contact_email: firstString(row, ['contact_email', 'email']),
    contact_phone: firstString(row, ['contact_phone', 'phone', 'phone_number']),
    status: normalizeClientStatus(row),
    since:
      firstString(row, ['since', 'created_at', 'updated_at']) ??
      '',
  }
}

function normalizeClientEnvelope(payload: unknown): IacrmListEnvelope<IacrmClient> {
  const data = extractCatalogRows(payload)
    .map((row) => normalizeClientItem(row))
    .filter((item): item is IacrmClient => item !== null)

  return {
    data,
    meta: { total: data.length },
  }
}

// ---------------------------------------------------------------------------
// Connection test
// ---------------------------------------------------------------------------

export async function testIacrmConnection(): Promise<{ ok: boolean; message: string }> {
  try {
    const catalog = await fetchIacrmServices()
    const count = catalog.data.length
    return {
      ok: true,
      message: count === 1
        ? 'Connection successful. 1 catalog item was returned by IACRM.'
        : `Connection successful. ${count} catalog items were returned by IACRM.`,
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Connection failed'
    return { ok: false, message: msg }
  }
}

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

export async function fetchIacrmServices() {
  return apiRequest<IacrmListEnvelope<IacrmService>>('/v1/iacrm/services')
}

export async function fetchIacrmService(serviceId: string) {
  const catalog = await fetchIacrmServices()
  const service = catalog.data.find((item) => item.iacrm_id === serviceId)

  if (!service) {
    throw new IacrmApiError(404, `IACRM catalog item ${serviceId} was not found.`)
  }

  return { data: service }
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export async function fetchIacrmClients() {
  return apiRequest<IacrmListEnvelope<IacrmClient>>('/v1/iacrm/clients')
}

export async function syncIacrmClients() {
  return apiRequest<{ message: string; result: { created: number; updated: number; skipped: number; failed: number } }>('/v1/iacrm/sync/clients', { method: 'POST' })
}

export async function syncIacrmServices() {
  return apiRequest<{ message: string; result: { created: number; updated: number; skipped: number; removed: number; failed: number } }>('/v1/iacrm/sync/services', { method: 'POST' })
}

export async function syncIacrmBusinesses() {
  return apiRequest<{ message: string; result: { created: number; updated: number; skipped: number; failed: number; removed: number } }>('/v1/iacrm/sync/businesses', { method: 'POST' })
}

export async function fetchIacrmClient(clientId: string) {
  const payload = await iacrmRequest<unknown>(`/clients/${clientId}`)
  const normalized = normalizeClientEnvelope(payload)
  const client = normalized.data[0] ?? null

  if (!client) {
    throw new IacrmApiError(404, `IACRM client ${clientId} was not found.`)
  }

  return { data: client }
}

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

export async function fetchIacrmPipelineProspects(stage?: string) {
  const query = stage ? `?stage=${encodeURIComponent(stage)}` : ''
  return iacrmRequest<IacrmListEnvelope<IacrmPipelineProspect>>(`/pipeline/prospects${query}`)
}

export async function fetchIacrmPipelineStages() {
  return iacrmRequest<{ data: IacrmPipelineStageSummary[] }>('/pipeline/stages')
}

// ---------------------------------------------------------------------------
// Facturation / Invoices
// ---------------------------------------------------------------------------

export async function fetchIacrmInvoices(status?: string) {
  const query = status ? `?status=${encodeURIComponent(status)}` : ''
  return iacrmRequest<IacrmListEnvelope<IacrmInvoice>>(`/invoices${query}`)
}

export async function fetchIacrmInvoiceSummary() {
  return iacrmRequest<IacrmDetailEnvelope<IacrmInvoiceSummary>>('/invoices/summary')
}

// ---------------------------------------------------------------------------
// Pipeline — write operations
// ---------------------------------------------------------------------------

export interface IacrmCreateProspectPayload {
  contact_name: string
  company_name?: string | null
  stage?: IacrmPipelineStage
  assigned_agent?: string | null
}

export async function createIacrmProspect(payload: IacrmCreateProspectPayload) {
  return iacrmRequest<IacrmDetailEnvelope<IacrmPipelineProspect>>('/pipeline/prospects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, stage: payload.stage ?? 'suspect' }),
  })
}

export async function moveIacrmProspectStage(
  iacrm_id: string,
  stage: IacrmPipelineStage,
  reason?: string,
) {
  return iacrmRequest<IacrmDetailEnvelope<IacrmPipelineProspect>>(
    `/pipeline/prospects/${encodeURIComponent(iacrm_id)}/stage`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage, reason: reason ?? null }),
    },
  )
}

// ---------------------------------------------------------------------------
// Platform (superadmin) — cross-business IACRM overview
// ---------------------------------------------------------------------------

export async function fetchIacrmPlatformBusinesses() {
  return apiRequest<IacrmListEnvelope<IacrmPlatformBusiness>>('/v1/iacrm/platform/businesses')
}

export async function fetchIacrmPlatformBusiness(businessId: string) {
  return iacrmRequest<{ data: IacrmPlatformBusiness }>(`/platform/businesses/${encodeURIComponent(businessId)}`)
}

export async function fetchIacrmPlatformBusinessServices(businessId: string) {
  return iacrmRequest<IacrmListEnvelope<IacrmService>>(`/platform/businesses/${encodeURIComponent(businessId)}/services`)
}

export async function fetchIacrmPlatformBusinessClients(businessId: string) {
  return iacrmRequest<IacrmListEnvelope<IacrmClient>>(`/platform/businesses/${encodeURIComponent(businessId)}/clients`)
}

export async function fetchIacrmPlatformBusinessPipelineProspects(businessId: string, stage?: string) {
  const query = stage ? `?stage=${encodeURIComponent(stage)}` : ''
  return iacrmRequest<IacrmListEnvelope<IacrmPipelineProspect>>(
    `/platform/businesses/${encodeURIComponent(businessId)}/pipeline/prospects${query}`,
  )
}

export async function fetchIacrmPlatformBusinessPipelineStages(businessId: string) {
  return iacrmRequest<{ data: IacrmPipelineStageSummary[] }>(
    `/platform/businesses/${encodeURIComponent(businessId)}/pipeline/stages`,
  )
}

export interface IacrmRequestLogFilters {
  limit?: number
  status?: string
  actor_type?: string
  direction?: string
  source?: string
  method?: string
  search?: string
}

export async function fetchIacrmRequestLogs(filters: IacrmRequestLogFilters = {}) {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(key, String(value))
    }
  })

  const query = params.toString()

  return apiRequest<IacrmListEnvelope<IacrmRequestLogEntry> & { meta?: { total: number; limit: number } }>(
    `/v1/iacrm/request-logs${query ? `?${query}` : ''}`,
  )
}
